import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/integrations/supabase/client";

const SignUpSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("waitlist").insert({ email: email.trim().toLowerCase() });
    setLoading(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("You're already on the list!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      return;
    }
    toast.success("You're on the list! We'll be in touch soon.");
    navigate("/thank-you");
  };

  return (
    <section
      id="reserve"
      className="relative py-24 md:py-32 overflow-hidden bg-[hsl(var(--pastel-mint))]"
    >
      <div ref={ref} className="relative z-10 mx-auto max-w-xl px-6 text-center">
        <p
          className={`text-[11px] font-bold uppercase tracking-[0.2em] text-foreground/50 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          Coming soon
        </p>
        <h2
          className={`mt-3 heading-bold text-4xl leading-[0.95] text-foreground sm:text-5xl lg:text-[4rem] transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "120ms" }}
        >
          Choose your{" "}
          <span className="font-display italic normal-case font-normal">state.</span>
        </h2>
        <p
          className={`mx-auto mt-5 max-w-md text-base leading-relaxed text-foreground/60 sm:text-lg transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "240ms" }}
        >
          We're launching soon. Join the waitlist for early access and exclusive drops.
        </p>

        <div
          className={`mt-8 transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "360ms" }}
        >
          <form onSubmit={handleSubmit} className="mx-auto max-w-sm">
            <div className="flex flex-col gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-[3.25rem] rounded-xl border-foreground/15 bg-background text-foreground placeholder:text-foreground/30 transition-shadow duration-200 focus:shadow-md"
              />
            </div>
            <Button
              type="submit"
              variant="hero"
              disabled={loading}
              className="mt-4 w-full h-[3.25rem] text-base active:scale-[0.97] hover:scale-[1.01] transition-all duration-200"
            >
              {loading ? "Joining..." : "Join the Waitlist"}
            </Button>
            <p className="mt-3 text-xs text-foreground/40 font-medium">
              🔥 1,247 people already on the list
            </p>
            <p className="mt-1 text-[11px] text-foreground/30">
              No spam. Unsubscribe anytime.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignUpSection;
